<template>
  <div class="px-3">
    <v-dialog
      v-model="showDeleteAlert"
      max-width="480px"
    >
      <videon-card heading="Delete Alert" showClose="true" @close="showDeleteAlert = false">
        <v-card-text>
          <v-form @submit.prevent="confirmDeleteAlert">
            <p class="subtitle-1 mt-3">
              Are you sure you want to delete this alert?
            </p>
            <div class="text-right">
              <v-btn
                class="ma-1"
                color="secondary"
                @click="showDeleteAlert = false"
                text
              >
                Cancel
              </v-btn>
              <v-btn
                class="ma-1"
                color="primary"
                @click="confirmDeleteAlert"
              >
                Delete
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    <v-dialog
      v-model="showNewAlert"
      max-width="640px"
    >
      <videon-card heading="Create Alert" showClose="true" @close="showNewAlert = false">
        <v-card-text>
          <v-form @submit.prevent="createAlert">
            <v-select
              v-model="newAlert.name"
              :items="availableAlerts"
              item-text="label"
              item-value="name"
              label="Alert"
              @change="newAlertTypeChange"
              :disabled="!canEdit"
              outlined
              hide-details
            ></v-select>
            
            <div v-if="newAlert.name == 'offline'">
              <v-alert
                class="mt-5"
                type="info"
                outlined
              >
                Device offline alerting can be managed via <a class="text-decoration-underline" @click="$router.push('/alerts/config')">Legacy Alert Settings</a>.
              </v-alert>
            </div>
            <div v-else>
              <v-text-field
                class="mt-5"
                v-model="newAlert.label"
                label="Alert Name"
                :disabled="!canEdit"
                outlined
                hide-details
              ></v-text-field>
            
              <div class="mt-6" v-if="newAlert">
                <generic-alert-document :alert="newAlert" :template="true" :device_guid="'org:' + org_guid" :canEdit="canEdit" :isNew="true" @configUpdate="newAlertUpdate" :key="newAlert.alert_guid"/>
              </div>
            
              <div class="text-right">
                <v-btn
                  class="ma-1"
                  color="secondary"
                  @click="showNewAlert = false"
                  text
                >
                  Cancel
                </v-btn>
                <v-btn
                  class="ma-1"
                  color="primary"
                  @click="createAlert"
                  :disabled="newAlertHasValidationErrors"
                >
                  Create Alert
                </v-btn>
              </div>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    
    <v-card flat>
      <v-card-title>
        Organization Alert Configuration
        <v-spacer/>
        
        <v-btn
          text
          small
          class="ma-1"
          @click="$router.push('/alerts')"
        >
          <v-icon>mdi-arrow-left</v-icon> Back
        </v-btn>
        
        <v-btn
          small
          color="primary"
          outlined
          @click="newAlertShow()"
          class="ma-1"
          v-if="organization.canEdit()"
        >
          <v-icon>mdi-plus</v-icon> Create Alert
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-alert dense outlined type="error" v-if="shadowTemplate.error">
            {{ shadowTemplate.errorText }}
          </v-alert>
          
          <v-data-table
            :headers="headers"
            :items="shadowTemplate.state"
            :single-expand="true"
            :expanded.sync="expanded"
            item-key="alert_guid"
            :items-per-page="100"
            :loading="shadowTemplate.loading"
            show-expand
            hide-default-footer
            @click:row="(item, slot) => slot.expand(!slot.isExpanded)"
          >
            <template
              v-slot:item.alert_guid="{ item }"
            >
              <span :class="(item.new) ? 'text--disabled' : ''">{{ alertName(item) }}</span>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="px-8 pt-8 pb-5">
                <generic-alert-document :alert="item" :template="true" :device_guid="'org:' + org_guid" :canEdit="canEdit" @configUpdate="configUpdate" @deleteAlert="deleteAlert(item)" :key="item.alert_guid"/>
              </td>
            </template>
            <template
              v-slot:item.config.enabled="{ item }"
            >
              <span :class="(item.config.enabled) ? 'primary--text' : 'text--disabled'">{{ (item.config.enabled) ? 'Yes' : 'No' }}</span>
            </template>
            <template
              v-slot:item.category="{ item }"
            >
              <span class="text-capitalize">{{ item.category }}</span> Alert
            </template>
            <template
              v-slot:item.alert_state="{ item }"
            >
              <span v-if="!item.alert_state" class="text--disabled">Unknown</span>
              <span v-else :class="(item.alert_state && item.alert_state.toLowerCase() == 'ok') ? 'green--text' : 'red--text'" class="text-capitalize">{{ (item.alert_state) ? $helpers.getLabel(item.alert_state) : '' }}</span>
            </template>
          </v-data-table>
        </v-container>
        <div class="text-right" v-if="canEdit">
          <v-btn
            color="primary"
            class="ma-1"
            @click="saveTemplate()"
            :disabled="!hasChanges"
          >
            Save
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
  import { mapGetters } from 'vuex'
  import GenericAlertDocument from '../device/shadow/encoder/documents/GenericAlertDocument.vue'
  
  
  export default {
    name: 'OrgAlertTemplates',
        
    components: {
      GenericAlertDocument
    },
    
    data() {
      return {
        expanded: [],
        
        hasChanges: false,
        expectingChanges: false,
        hasValidationError: false,
        
        showDeleteAlert: false,
        
        selectedAlert: false,
        
        showNewAlert: false,
        
        availableAlerts: [
          {'name': 'thermal_system_temp_current', 'label': 'High Temperature', 'type': 'metric', 'threshold_value': 80, 'threshold_condition': 'GreaterThan', 'notify_on_close': true, 'user_list': [], 'email_list': []},
          {'name': 'cpu_percent_used', 'label': 'High CPU Utilization', 'type': 'metric', 'threshold_value': 90, 'threshold_condition': 'GreaterThan', 'notify_on_close': true, 'user_list': [], 'email_list': []},
          {'name': 'memory_system_percent_used', 'label': 'High Memory Utilization', 'type': 'metric', 'threshold_value': 90, 'threshold_condition': 'GreaterThan', 'notify_on_close': true, 'user_list': [], 'email_list': []},
          {'name': 'filesystem_userdata_percent_used', 'label': 'High Filesystem Utilization', 'type': 'metric', 'threshold_value': 90, 'threshold_condition': 'GreaterThan', 'notify_on_close': true, 'user_list': [], 'email_list': []},
          {'name': 'video_input_format', 'label': 'Undefined Input Video Format', 'type': 'inputs', 'threshold_value': 'FORMAT_UNDEFINED', 'threshold_condition': 'EqualTo', 'notify_on_close': true, 'user_list': [], 'email_list': []},
          {'name': 'audio_input_detected', 'label': 'Undefined Audio Format', 'type': 'inputs', 'threshold_value': false, 'threshold_condition': 'EqualTo', 'notify_on_close': true, 'user_list': [], 'email_list': []},
          {'name': 'status_code', 'label': 'Output Error', 'type': 'outputs', 'threshold_value': 'RUNNING', 'threshold_condition': 'NotEqualTo', 'notify_on_close': true, 'user_list': [], 'email_list': []},
          {'name': 'status', 'label': 'Encoder Error', 'type': 'encoders', 'threshold_value': 'RUNNING', 'threshold_condition': 'NotEqualTo', 'notify_on_close': true, 'user_list': [], 'email_list': []},
          {'name': 'offline', label: 'Device Offline'}
        ],
        
        newAlert: false,
        newAlertHasValidationErrors: false,
        
        headers: [
          { text: 'Alert Name', align: 'start', value: 'alert_guid', sortable: false },
          { text: 'Category', align: 'center', value: 'category', sortable: false },
          { text: 'Enabled', align: 'center', value: 'config.enabled', sortable: false },
        ],
        
      }
    },
    
    computed: {
      ...mapGetters('user', ['user', 'guid', 'organization', 'isOrgAdmin']),
      
      orgName() {
        if (this.organization && this.organization.org_name) {
          return this.organization.org_name
        }
        return 'Unknown Org'
      },
      
      org_guid() {
        if (this.organization) {
          return this.organization.org_guid
        }
        return false
      },
      
      shadowTemplate() {
        if (this.organization.org_guid) {
          return this.$shadowTemplates.getTemplate('org', this.org_guid, 'Alerts')
        }
        
        return false
      },
      
      canEdit() {
        return this.organization.canEdit()
      }
    },
    
    mounted() {
      
    },
    
    methods: {
      alertName(item) {
        if (item.label || item.config.label) {
          return item.label || item.config.label
        } else if (item.name || item.config.name) {
            return item.name || item.config.name
        } else if (item.new) {
          return 'New Alert'
        } else if (item.alert_guid) {
          return 'Alert #' + item.alert_guid
        }
        return 'Alert N'
      },
      
      newAlertShow() {
        this.newAlert = { 
          'alert_guid': this.$helpers.uuidv4(),
          'type': '',
          'name': '',
          'label': '',
          'category': 'org',
          'config': {},
          'new': true
        }
        this.showNewAlert = true
      },
      
      newAlertTypeChange() {
        this.$delete(this.newAlert, 'config')
        
        var alertSkeleton = this.availableAlerts.find(x => x.name == this.newAlert.name)
        if (alertSkeleton) {
          this.newAlert.type = alertSkeleton.type
          this.newAlert.label = alertSkeleton.label
          
          this.newAlert.config = {
            enabled: false,
            threshold_value: alertSkeleton.threshold_value,
            threshold_condition: alertSkeleton.threshold_condition,
            notify_on_close: alertSkeleton.notify_on_close, 
            user_list: alertSkeleton.user_list,
            email_list: alertSkeleton.email_list,
          }
        }
        
        console.log('OrgAlertTemplates newAlertTypeChange', this.newAlert)
      },
      
      newAlertUpdate(config, hasValidationError = false) {
        console.log('OrgAlertTemplates newAlertUpdate', config, hasValidationError)
        this.newAlert.config = config
        this.newAlertHasValidationErrors = hasValidationError
      },
      
      createAlert() {
        if (!this.newAlertHasValidationErrors) {
          console.log('OrgAlertTemplates createAlert', this.newAlert)
          this.shadowTemplate.state.push(Object.assign({}, this.newAlert))
          this.hasChanges = true
          this.expanded = []
          
          this.showNewAlert = false
        }
      },
      
      
      deleteAlert(item) {
        this.selectedAlert = item
        this.showDeleteAlert = true
      },
      confirmDeleteAlert() {
        var alertIndex = this.shadowTemplate.state.indexOf(this.selectedAlert)
        if (alertIndex !== -1) {
          console.log('OrgAlertTemplates confirmDeleteAlert deleting alert', this.selectedAlert)
          
          this.shadowTemplate.state.splice(alertIndex, 1)
          this.hasChanges = true
          this.expanded = []
        }
        this.showDeleteAlert = false
      },
      
      
      configUpdate(config, hasValidationError = false) {
        this.hasChanges = true
        this.hasValidationError = hasValidationError
      },
      
      saveTemplate() {
        var state = this.shadowTemplate.state.map((alertItem) => {
          delete alertItem.new
          return alertItem
        })
        
        this.shadowTemplate.saveTemplate(state)
        this.expanded = []
        this.hasChanges = false
        this.expectingChanges = true
      }
    }
  }
</script>